import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import SearchField from '../../common/SearchField';
import Popover from '../../common/Popover';
import AnnotationUtil from '../../queries/annotations/AnnotationUtil';
import ThumbnailUtil from '../../util/ThumbnailUtil';
import ViewUtil from '../../ViewUtil';
import {
  Annotation,
  AnnotationThread,
  FuidToUnreadComments,
  LsZmCollectionAssetsResponse,
  RepoItem,
  FuidRrnInfo
} from '../../autogenerated/ZoomBridge';
import HashMap from './../../protocol/HashMap';
import { TextWithEllipsisFileExtension } from '../../common/TextWithEllipsis';
import Dropdown from '../../common/Dropdown';
import LsZmCollectionAssets from './../../queries/lightbox/assets/LsZmCollectionAssets';
import HashSet from '../../protocol/HashSet';
import Store from '../../store/Store';
import UnreadCommentsData from '../../queries/annotations/UnreadCommentsData';
import {
  ACTIVE_COMMENT_ID,
  MULTIPAGE_ASSET_CURRENT_PAGE,
  COMMENT_FILTER_BY,
  SELECTED_ASSET,
  COLLECTIONS,
  SELECTED_COLLECTION,
  COMMENTS
} from '../../store/Constants';
import FileInfoUtil from '../../util/FileInfoUtil';
import { CommentFilterOption, SUBTAB } from '../../util/Constants';
import GetAnnotations from '../../queries/annotations/GetAnnotations';
import t from '../../i18n/I18n';
import './UnreadComments.css';
import CommentUtil from '../../util/CommentUtil';

const debug = require('../../debug/debug')('wc:panels:UnreadComments');

class UnreadComments extends React.Component {
  static propTypes = {
    onClose: PropTypes.func
  };

  static defaultProps = {
    onClose: _.noop
  };

  constructor(props) {
    super(props);

    this.state = {
      searchText: '',
      collectionId: null,
      commentFeedback: null,
      markedAll: false,
      userId2FullName: {},
      collectionUnreadCountEntries: []
    };
  }

  getAuthorFullName(userId) {
    if (!this.state.userId2FullName[userId]) {
      ThumbnailUtil.loadUserFullName(userId)
        .then((fullName) => {
          const newObject = _.clone(this.state.userId2FullName);
          newObject[userId] = fullName;
          this.setState({ userId2FullName: newObject });
        })
        .catch(_.noop);
      this.state.userId2FullName[userId] = userId;
    }
    return this.state.userId2FullName[userId];
  }

  componentWillMount() {
    this.setEntriesAndCollectionId();
    Store.addListener(UnreadCommentsData.NAME, this.onUnreadCommentsDataChange);
  }

  componentWillUnmount() {
    Store.removeListener(UnreadCommentsData.NAME, this.onUnreadCommentsDataChange);
  }

  onUnreadCommentsDataChange = (storeDataChangeEvent) => {
    this.setEntriesAndCollectionId();
  };

  componentWillUpdate() {
    const unreadCommentsData: UnreadCommentsData = Store.get(UnreadCommentsData.NAME);
    const collectionUnreadCountEntries = unreadCommentsData
      ? unreadCommentsData.getCollectionUnreadCountEntries()
      : undefined;

    const didChange =
      collectionUnreadCountEntries !== this.state.collectionUnreadCountEntries ||
      _.size(collectionUnreadCountEntries) !== this.state.collectionUnreadCountEntries.length;

    if (didChange) {
      this.setEntriesAndCollectionId();
    }
  }

  setEntriesAndCollectionId = () => {
    const { collectionId } = this.state;
    const unreadCommentsData: UnreadCommentsData = Store.get(UnreadCommentsData.NAME);
    const collectionUnreadCountEntries = unreadCommentsData
      ? unreadCommentsData.getCollectionUnreadCountEntries()
      : undefined;
    let nextState = {
      collectionUnreadCountEntries
    };

    const didCollectionIdChange =
      !collectionId ||
      (!_.isEmpty(collectionUnreadCountEntries) &&
        !collectionUnreadCountEntries.find((e) => e._collectionId === collectionId));
    if (didCollectionIdChange) {
      nextState.collectionId =
        collectionUnreadCountEntries.length > 0 ? collectionUnreadCountEntries[0].getCollectionId() : null;
    }

    this.setState(nextState);
  };

  navigateToComment = async (fuidToUnreadComments: FuidToUnreadComments, threadId: number, commentId: number) => {
    try {
      debug('[navigateToComment()] args: ', { fuidToUnreadComments, threadId, commentId });

      const collectionId = fuidToUnreadComments.__collectionId;
      debug('[navigateToComment()] fetching assets. collectionId: ', collectionId);
      const lsZmCollectionAssets = new LsZmCollectionAssets(collectionId);
      lsZmCollectionAssets.setFuidFilter([fuidToUnreadComments.getFuidRrnInfo().getFuid()]);
      const res: LsZmCollectionAssetsResponse = await lsZmCollectionAssets.run();
      debug('[navigateToComment()] response: ', res);

      const files: RepoItem[] = res.getResultFiles();
      if (_.size(files) !== 1) {
        window.setError(t('COULD_NOT_FIND_ASSET_TEXT'));
        throw new Error(t('COULD_NOT_FIND_ASSET_SHORT_TEXT'));
      }

      const asset = files[0];
      window.gs.selectedAssets = [asset];

      const fuidRrnInfo: FuidRrnInfo = AnnotationUtil.repoItemToFuidRrnInfo(asset);
      debug('[navigateToComment()] fetching comments. fuidRrnInfo: ', fuidRrnInfo);
      AnnotationUtil.getAnnotations(fuidRrnInfo, collectionId, 0, (getAnnotations: GetAnnotations) => {
        const annotations = getAnnotations.getAnnotations() || [];
        debug('[navigateToComment()] response: ', getAnnotations);
        let comment = annotations.find((c) => c.getThreadId() === threadId);
        CommentUtil.setActiveCommentThreadTime(comment);
        debug('[navigateToComment()] target comment: ', comment);
        Store.put(ACTIVE_COMMENT_ID, threadId);
        Store.put(COMMENT_FILTER_BY, CommentFilterOption.ALL);
        this.setCollectionAndAsset(collectionId, asset);
        if (FileInfoUtil.isMultiPage(asset)) {
          Store.put(MULTIPAGE_ASSET_CURRENT_PAGE, AnnotationUtil.getPageNumber(comment));
        }

        ViewUtil.setARGS({
          activeTab: 'COLLECTIONS',
          activeSubTab: SUBTAB.ALL.KEY,
          collectionId,
          fuid: asset.getFuid(),
          tId: threadId,
          cId: commentId
        });

        setTimeout(() => {
          ViewUtil.setTitle();
        }, 1000);
      });
    } catch (err) {
      debug('[navigateToComment()] error: ', err);
    }
  };

  setCollectionAndAsset(commentCollectionId: Number, asset) {
    let collection: ZmCollection = (Store.get(COLLECTIONS) || []).find(
      (c) => c.getCollectionId() === commentCollectionId
    );
    debug('[setCollection()] collection: ', collection);
    Store.put(SELECTED_COLLECTION, collection);
    Store.put(SELECTED_ASSET, asset);
  }

  markCommentAsRead = (fuidToUnreadComments, threadId, commentId) => {
    fuidToUnreadComments.getUnreadCommentsIds().remove(commentId);
    this.setState({ commentFeedback: { threadId, commentId } });
    this.delay(() => this.setState({ commentFeedback: null }), 2000);

    AnnotationUtil.markCommentAsRead(
      undefined,
      [commentId],
      fuidToUnreadComments.__collectionId,
      fuidToUnreadComments.getFuidRrnInfo()
    )
      .then((res) => {
        AnnotationUtil.loadAndRenderUnreadCommentDetailInWindowGlobalObject();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  onCollectionClick = (collectionId) => {
    this.setState({ collectionId: collectionId });
  };

  onCommentClick = (fuidToUnreadComments, threadId, commentId, isUnread) => {
    if (isUnread) {
      // this.markCommentAsRead(fuidToUnreadComments, threadId, commentId);
    }

    this.navigateToComment(fuidToUnreadComments, threadId, commentId);
  };

  onMarkAllClick = () => {
    const { collectionUnreadCountEntries, collectionId } = this.state;
    const threadIds2MarkAsRead: number[] = [];
    const unreadCommentsIds = [];
    collectionUnreadCountEntries.forEach((entry) => {
      if (entry.getCollectionId() === collectionId) {
        entry.getFuidUnreadComments().forEach((fuidToUnreadComments) => {
          const commentId2ThreadId: HashMap = fuidToUnreadComments.getCommentIdToThreadId();
          if (commentId2ThreadId) {
            threadIds2MarkAsRead.push(...commentId2ThreadId.values());
          }
          const unreadCommentsSet: HashSet = fuidToUnreadComments.getUnreadCommentsIds();
          if (unreadCommentsSet && unreadCommentsSet.size()) {
            unreadCommentsIds.push(...unreadCommentsSet.toArray());
          }
        });
      }
    });

    debug('[onMarkAllClick()] Marking threadIds as read: ', threadIds2MarkAsRead, 'for collection id: ', collectionId);

    AnnotationUtil.markCommentAsRead(undefined, unreadCommentsIds)
      .then((response) => {
        AnnotationUtil.loadAndRenderUnreadCommentDetailInWindowGlobalObject()
          .then(_.noop)
          .catch(_.noop);
      })
      .catch((e) => {
        this.logger.error(e);
      });

    // this.setState({ markedAll: true });
    // this.delay(() => this.setState({ markedAll: false }), 2000);

    // TODO: modify this as needed depending on what backend needs
    /* AnnotationUtil.markCommentAsRead()
      .then(res => {
        // no op
      })
      .catch(err => {
        console.error(err);
      }); */
  };

  delay = (fn, timeout) => {
    setTimeout(() => {
      if (this._node) {
        fn();
      }
    }, timeout);
  };

  /**
   * Recursively processes an array of comments. Returns true if
   * searchText is found somewhere in the comment tree; false otherwise.
   */
  isSearchTextInCommentTree = (comments: AnnotationThread[], searchText: string) => {
    if (_.size(comments) === 0) {
      return;
    }

    const matchingCommentIds = [];
    comments.forEach((comment) => {
      if (
        comment.getText() &&
        comment
          .getText()
          .toLowerCase()
          .indexOf(searchText) > -1
      ) {
        matchingCommentIds.push(comment.getCommentId());
      }
      if (_.size(comment.getReplies()) > 0) {
        const result = this.isSearchTextInCommentTree(comment.getReplies(), searchText);
        if (_.size(result) > 0) {
          matchingCommentIds.push(...result);
        }
      }
    });

    return matchingCommentIds;
  };

  /**
   * Returns an array of fuids that contain searchText somewhere
   * in their comment tree.
   */
  getSearchTextLocations = (fuidToUnreadCommentsArray: Map<number, FuidToUnreadComments[]>, searchText: string) => {
    let locations: Map<number, FuidToUnreadComments[]> = undefined;
    fuidToUnreadCommentsArray.forEach((fuidToUnreadComments) => {
      const comments: Annotation[] = fuidToUnreadComments.getAnnotations();
      const matchingCommentIds: number[] = this.isSearchTextInCommentTree(comments, searchText);
      if (_.size(matchingCommentIds) > 0) {
        const fuid = fuidToUnreadComments.getFuidRrnInfo().getFuid();
        if (!locations) {
          locations = new Map();
        }
        locations.set(fuid, matchingCommentIds);
      }
    });

    return locations;
  };

  renderComments = (fuidToUnreadComments: FuidToUnreadComments, filterCommentIds: number[]) => {
    const comments = fuidToUnreadComments.getAnnotations().map((comment) => {
      const commentId = comment.getCommentId();
      const threadId = fuidToUnreadComments.getCommentIdToThreadId().get(commentId);
      const parentId = comment.getParentCommentId();
      if (!threadId) {
        debug('[renderComments()] no threadId in commentIdToThreadId map for commentId: ', commentId);
      }
      if (_.size(filterCommentIds) > 0) {
        if (!_.includes(filterCommentIds, commentId)) {
          return null;
        }
      }
      const src = ThumbnailUtil.avatar(comment.getCreator());
      const isUnread = fuidToUnreadComments.getUnreadCommentsIds().contains(commentId);
      let className = 'comment flex-row align-center';
      if (isUnread) {
        className += ' unread';
      }

      const { commentFeedback } = this.state;
      const showFeedback =
        commentFeedback && commentFeedback.threadId === threadId && commentFeedback.commentId === commentId;
      const isReviewComment = AnnotationUtil.isReviewAnnotationComment(comment);
      const isPost = comment.getCommentId() === threadId || parentId === 0;
      const author = this.getAuthorFullName(comment.getCreator());
      let displayText = '';

      if (isReviewComment) {
        const status = comment.getText() ? comment.getText().toLowerCase() : '';
        displayText = t('REVIEW_COMMENT_NOTIF', { STATUS: status });
      } else {
        displayText = isPost ? t('POSTED_NEW_COMMENT_NOTIF') : t('REPLIED_COMMENT_NOTIF');
      }

      return (
        <li
          key={`${threadId}-${commentId}`}
          className={className}
          onClick={() => this.onCommentClick(fuidToUnreadComments, threadId, commentId, isUnread)}>
          <img src={src} alt={comment.getCreator()} onError={(e) => e.target.src = 'images/blank_profile_pic.png'} />
          <div className="info flex-main">
            <div className="summary">
              <b>{author}</b> {displayText}
            </div>
            {isReviewComment ? null : <div className="text">"{comment.getText()}"</div>}
          </div>
          {showFeedback ? (
            <Popover top="-22px" left="100px" position="BOTTOM">
              {t('MARKED_AS_READ')}
            </Popover>
          ) : null}
        </li>
      );
    });

    return <ul className="comments">{comments}</ul>;
  };

  renderAssets = () => {
    const { collectionUnreadCountEntries } = this.state;
    if (_.size(collectionUnreadCountEntries) == 0) {
      return null;
    }

    const fuidToUnreadCommentsArray = collectionUnreadCountEntries.reduce((arr, entry) => {
      const fuidToUnreadCommentsArray = entry.getFuidUnreadComments().map((fuidToUnreadComments) => {
        fuidToUnreadComments.__collectionId = entry.getCollectionId();
        return fuidToUnreadComments;
      });

      return arr.concat(fuidToUnreadCommentsArray);
    }, []);

    const { collectionId } = this.state;
    const searchText = this.state.searchText.toLowerCase();
    let searchTextLocations: Map<number, number[]> = undefined;
    let filteredFuidToUnreadCommentsArray = collectionId
      ? fuidToUnreadCommentsArray.filter((a) => a.__collectionId === collectionId)
      : fuidToUnreadCommentsArray;

    if (searchText.trim()) {
      searchTextLocations = this.getSearchTextLocations(filteredFuidToUnreadCommentsArray, searchText);
      if (_.size(searchTextLocations) == 0) {
        return null;
      }
    }

    const markup = filteredFuidToUnreadCommentsArray.map((fuidToUnreadComments) => {
      if (fuidToUnreadComments.getAnnotations().length === 0) {
        return null;
      }
      const { _fuid, _rrn } = fuidToUnreadComments.getFuidRrnInfo();
      if (_.size(searchTextLocations) > 0 && !searchTextLocations.has(_fuid)) {
        return null;
      }

      return (
        <li key={`${_fuid}-${_rrn}`} className="asset">
          <div className="name">
            {/* <TextWithEllipsis text={fuidToUnreadComments.getFileName()} /> */}
            <TextWithEllipsisFileExtension
              title={fuidToUnreadComments.getFileName()}
              text={fuidToUnreadComments.getFileName()}
            />
          </div>
          {this.renderComments(fuidToUnreadComments, searchTextLocations ? searchTextLocations.get(_fuid) : null)}
        </li>
      );
    });

    return <ul className="assets">{markup}</ul>;
  };

  renderCollections = () => {
    const { collectionId } = this.state;
    const { collectionUnreadCountEntries } = this.state;
    if (!collectionUnreadCountEntries.length) {
      return <div style={{ color: '#7A7A7A', textAlign: 'center' }}>{t('NO_UNREAD_COMMENTS')}</div>;
    }

    const collections = collectionUnreadCountEntries.map((entry) => {
      const commentCount = entry.getUnreadCount();
      const subLabel = commentCount > 0 ? `${commentCount} new` : '';

      return {
        value: entry.getCollectionId(),
        label: entry.getCollectionName(),
        subLabel
      };
    });

    return (
      <div className="collections">
        <div className="label">{t('CHOOSE_A_COLLECTION')}</div>
        <Dropdown
          value={collectionId}
          options={collections}
          onChange={this.onCollectionClick}
          ellipsis
          constrainWidth
        />
      </div>
    );
  };

  render() {
    return (
      <div name="UnreadComments" ref={(node) => (this._node = node)}>
        {this.renderCollections()}
        <SearchField value={this.state.searchText} onChange={(searchText) => this.setState({ searchText })} />
        <div>
          <button className="mark-all" onClick={this.onMarkAllClick}>
            {t('MARK_ALL_AS_READ')}
          </button>
          {this.state.markedAll ? (
            <div className="mark-all-popover">
              <Popover top="-12px" left="4px" style={{ whiteSpace: 'nowrap' }}>
                {t('MARKED_ALL_AS_READ')}
              </Popover>
            </div>
          ) : null}
        </div>
        {this.renderAssets()}
      </div>
    );
  }
}

export default UnreadComments;
